import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  ArrowLeft,
  Heart,
  LoaderCircle,
  ShoppingBag,
  Store,
  MessageCircle,
  Plus,
  Minus,
  Trash2,
  X,
  CheckCircle2,
  ShoppingCart,
  ShieldCheck,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { db } from "../firebase";
const money = (value = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve(window.Razorpay);
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => window.Razorpay ? resolve(window.Razorpay) : reject(new Error("Razorpay checkout did not load."));
  script.onerror = () => reject(new Error("Could not load Razorpay checkout."));
  document.body.appendChild(script);
});
const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf?.jsPDF) return resolve(window.jspdf.jsPDF);
  const existing = document.querySelector('script[data-vasthra-jspdf="true"]');
  if (existing) {
    existing.addEventListener("load", () => window.jspdf?.jsPDF ? resolve(window.jspdf.jsPDF) : reject(new Error("PDF library failed to load.")), { once: true });
    existing.addEventListener("error", () => reject(new Error("Could not load PDF library.")), { once: true });
    return;
  }
  const script = document.createElement("script");
  script.dataset.vasthraJspdf = "true";
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js";
  script.onload = () => window.jspdf?.jsPDF ? resolve(window.jspdf.jsPDF) : reject(new Error("PDF library failed to load."));
  script.onerror = () => reject(new Error("Could not load PDF library. Check your internet connection and try again."));
  document.head.appendChild(script);
});

const downloadInvoice = async (invoice) => {
  const JsPDF = await loadJsPDF();
  const pdf = new JsPDF({ unit: "mm", format: "a4" });
  const left = 16;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const maxWidth = pageWidth - left * 2;
  let y = 18;
  const addLine = (text, options = {}) => {
    const size = options.size || 10;
    pdf.setFont("helvetica", options.bold ? "bold" : "normal");
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(String(text ?? "-"), maxWidth);
    const needed = lines.length * (size * 0.45) + (options.gap ?? 2);
    if (y + needed > pageHeight - 16) { pdf.addPage(); y = 18; }
    pdf.text(lines, left, y);
    y += needed;
  };
  pdf.setTextColor(136, 19, 55);
  addLine("VASTHRA", { size: 22, bold: true, gap: 3 });
  pdf.setTextColor(40, 40, 40);
  pdf.setTextColor(22, 101, 52);
  addLine("PAID · PAYMENT INVOICE", { size: 14, bold: true, gap: 5 });
  pdf.setTextColor(40, 40, 40);
  addLine(`Order ID: ${(invoice.orderIds || []).join(", ") || "-"}`);
  addLine(`Razorpay Order ID: ${invoice.razorpayOrderId || "-"}`);
  addLine(`Payment ID: ${invoice.razorpayPaymentId || "-"}`);
  addLine(`Date: ${invoice.date || new Date().toLocaleString("en-IN")}`, { gap: 5 });
  addLine("BILL TO / SHIP TO", { bold: true, size: 11 });
  const customer = invoice.customer || {};
  addLine(customer.name || "Customer");
  addLine([customer.phone, customer.email].filter(Boolean).join(" | ") || "-");
  addLine([customer.address, customer.city, customer.pincode].filter(Boolean).join(", ") || "-");
  y += 3;
  pdf.setFillColor(220, 252, 231);
  pdf.rect(left, y - 4, maxWidth, 8, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("Item", left + 2, y + 1);
  pdf.text("Qty", left + 105, y + 1);
  pdf.text("Price", left + 123, y + 1);
  pdf.text("Amount", pageWidth - left - 2, y + 1, { align: "right" });
  y += 10;
  pdf.setFont("helvetica", "normal");
  (invoice.items || []).forEach((item) => {
    const name = String(item.productName || item.name || "Saree");
    const quantity = Number(item.quantity || 1);
    const price = Number(item.sellingPrice ?? item.publicPrice ?? item.price ?? 0);
    const lineTotal = Number(item.lineTotal ?? price * quantity);
    const nameLines = pdf.splitTextToSize(name, 95);
    const rowHeight = Math.max(7, nameLines.length * 4.5 + 2);
    if (y + rowHeight > pageHeight - 22) { pdf.addPage(); y = 18; }
    pdf.text(nameLines, left + 2, y);
    pdf.text(String(quantity), left + 105, y);
    pdf.text(price.toFixed(2), left + 123, y);
    pdf.text(lineTotal.toFixed(2), pageWidth - left - 2, y, { align: "right" });
    y += rowHeight;
    pdf.setDrawColor(230, 230, 230);
    pdf.line(left, y - 2, pageWidth - left, y - 2);
  });
  y += 5;
  if (y > pageHeight - 25) { pdf.addPage(); y = 18; }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text(`Total paid: INR ${Number(invoice.total || 0).toFixed(2)}`, pageWidth - left, y, { align: "right" });
  y += 12;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text("Payment verified by Razorpay. This is a computer-generated invoice.", left, Math.min(y, pageHeight - 12));
  const orderPart = (invoice.orderIds || []).join("-").replace(/[^a-zA-Z0-9_-]/g, "") || "order";
  pdf.save(`Vasthra-Invoice-${orderPart}.pdf`);
};
const isActiveProduct = (product) => {
  const status = String(product.status || "active").toLowerCase();
  const visibility = String(product.visibility || "live").toLowerCase();
  const stock = Number(product.stock ?? product.stockQuantity ?? 0);
  return (
    ["active", "live", "published", "approved"].includes(status) &&
    !["hidden", "inactive", "disabled", "archived", "draft"].includes(
      visibility,
    ) &&
    stock > 0
  );
};
export default function PublicStore() {
  const { slug = "" } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFabric, setSelectedFabric] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [storeError, setStoreError] = useState("");
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    pincode: "",
  });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [orderSuccess, setOrderSuccess] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");
  useEffect(() => {
    let active = true;
    let unsubscribeProducts = null;
    let unsubscribeSettings = null;
    async function loadStore() {
      setLoading(true);
      setStoreError("");
      setStore(null);
      setProducts([]);
      try {
        const normalizedSlug = String(slug).trim().toLowerCase();
        const storeSnapshot = await getDoc(
          doc(db, "publicStores", normalizedSlug),
        );
        if (!active) return;
        if (!storeSnapshot.exists()) {
          setStoreError("This store link does not exist.");
          setLoading(false);
          return;
        }
        const storeData = storeSnapshot.data();
        if (String(storeData.status || "").toLowerCase() !== "approved") {
          setStoreError(
            String(storeData.status || "").toLowerCase() === "rejected"
              ? "This store is currently unavailable."
              : "This store is awaiting approval and is not public yet.",
          );
          setLoading(false);
          return;
        }
        const resellerId =
          storeData.resellerId ||
          storeData.ownerUid ||
          storeData.uid ||
          storeData.userId ||
          storeData.id ||
          "";
        setStore({ ...storeData, slug: storeSnapshot.id, resellerId });
        if (!resellerId) {
          setStoreError(
            "This store is missing its reseller account link. Please contact support.",
          );
          setLoading(false);
          return;
        }
        const settingsQuery = query(
          collection(db, "resellerProductSettings"),
          where("resellerId", "==", resellerId),
          where("isListed", "==", true),
        );
        unsubscribeSettings = onSnapshot(
          settingsQuery,
          (settingsSnapshot) => {
            if (!active) return;
            const settings = settingsSnapshot.docs
              .map((item) => ({ id: item.id, ...item.data() }))
              .filter((item) => item.productId);
            if (!settings.length) {
              setProducts([]);
              setLoading(false);
              return;
            }
            if (unsubscribeProducts) unsubscribeProducts();
            const productIds = new Set(settings.map((item) => item.productId));
            unsubscribeProducts = onSnapshot(
              query(
                collection(db, "products"),
                where("status", "==", "active"),
              ),
              (snapshot) => {
                if (!active) return;
                const settingsById = Object.fromEntries(
                  settings.map((setting) => [setting.productId, setting]),
                );
                const productRows = snapshot.docs
                  .map((item) => ({ id: item.id, ...item.data() }))
                  .filter(
                    (product) =>
                      productIds.has(product.id) && isActiveProduct(product),
                  );
                setProducts(
                  productRows.map((product) => {
                    const setting = settingsById[product.id];
                    const basePrice = Number(
                      product.wholesalePrice ??
                        product.fixedPrice ??
                        product.price ??
                        0,
                    );
                    const savedSellingPrice = Number(setting?.sellingPrice);
                    return {
                      ...product,
                      shopOwnerId:
                        product.shopOwnerId || setting?.shopOwnerId || "",
                      resellerMargin: Number(setting?.margin ?? 0),
                      publicPrice:
                        Number.isFinite(savedSellingPrice) &&
                        savedSellingPrice > 0
                          ? savedSellingPrice
                          : basePrice + Number(setting?.margin ?? 0),
                    };
                  }),
                );
                setStoreError("");
                setLoading(false);
              },
              (error) => {
                console.error("Public store products error:", error);
                if (!active) return;
                setStoreError(
                  error.code === "permission-denied"
                    ? "Product access is blocked by Firestore rules."
                    : "Could not load sarees. Please try again.",
                );
                setLoading(false);
              },
            );
          },
          (error) => {
            console.error("Public store listing settings error:", error);
            if (!active) return;
            setStoreError(
              error.code === "permission-denied"
                ? "Store listings are blocked by Firestore rules."
                : "Could not load this store's catalogue.",
            );
            setLoading(false);
          },
        );
      } catch (error) {
        console.error("Public store load error:", error);
        if (!active) return;
        setStoreError(
          error.code === "permission-denied"
            ? "This store is not publicly readable yet. Check Firestore rules."
            : "Could not load this store. Please try again.",
        );
        setLoading(false);
      }
    }
    loadStore();
    return () => {
      active = false;
      if (unsubscribeProducts) unsubscribeProducts();
      if (unsubscribeSettings) unsubscribeSettings();
    };
  }, [slug]);
  // Route-specific SEO for public reseller storefronts.
  // This updates browser metadata after the store data has loaded.
  useEffect(() => {
    const siteUrl = "https://vasthra.online";
    const storeName = String(
      store?.storeName || store?.shopName || store?.name || "Saree Store",
    ).trim();
    const storeDescription = String(
      store?.storeDescription ||
        store?.tagline ||
        `Explore sarees at ${storeName}. Browse available designs and order online through Vasthra.`,
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 300);
    const pageTitle = store
      ? `${storeName} | Saree Collection on Vasthra`
      : "Saree Shopping & Reselling | Vasthra";
    const pageDescription = store
      ? `${storeDescription} Shop saree styles online through this Vasthra reseller store.`
          .slice(0, 300)
      : "Discover saree collections with Vasthra. Explore online saree shopping and start a saree reselling business by sharing products with customers and earning a margin on successful orders.";
    const canonicalUrl = store?.slug
      ? `${siteUrl}/s/${encodeURIComponent(store.slug)}`
      : `${siteUrl}/s/${encodeURIComponent(String(slug || "").toLowerCase())}`;

    document.title = pageTitle;

    const setMeta = (selector, attribute, key, content) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', "name", "description", pageDescription);
    setMeta('meta[name="robots"]', "name", "robots", store
      ? "index,follow,max-image-preview:large"
      : "noindex,follow");
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Vasthra");
    setMeta('meta[property="og:title"]', "property", "og:title", pageTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", pageDescription);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[property="og:image"]', "property", "og:image", `${siteUrl}/og-image.png`);
    setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", "Vasthra — Saree Shopping and Reselling");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", pageTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", pageDescription);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", `${siteUrl}/og-image.png`);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const schemaId = "vasthra-store-structured-data";
    let schema = document.getElementById(schemaId);
    if (!schema) {
      schema = document.createElement("script");
      schema.id = schemaId;
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Store",
      name: storeName,
      description: pageDescription,
      url: canonicalUrl,
      image: `${siteUrl}/og-image.png`,
      isPartOf: {
        "@type": "Organization",
        name: "Vasthra",
        url: `${siteUrl}/`,
      },
    });
  }, [slug, store, products.length]);

  const cartItems = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, quantity]) => {
          const product = products.find((item) => item.id === id);
          return product ? { ...product, quantity } : null;
        })
        .filter(Boolean),
    [cart, products],
  );
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const total = cartItems.reduce(
    (sum, item) => sum + Number(item.publicPrice) * item.quantity,
    0,
  );
  const addToCart = (product) => {
    if (!product?.id) return;
    setCart((current) => ({
      ...current,
      [product.id]: (Number(current[product.id]) || 0) + 1,
    }));
    setCartOpen(true);
  };
  const changeQuantity = (productId, delta) => {
    setCart((current) => {
      const nextQuantity = (Number(current[productId]) || 0) + Number(delta || 0);
      const next = { ...current };
      if (nextQuantity <= 0) delete next[productId];
      else next[productId] = nextQuantity;
      return next;
    });
  };
  const shareProduct = (product) => {
    const text = `Check out ${product.name || product.title || "this saree"} at ${money(product.publicPrice)}: ${window.location.origin}/s/${store?.slug}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const placeOrder = async (event) => {
    event.preventDefault();
    setOrderError("");
    if (!cartItems.length) return setOrderError("Your cart is empty.");
    if (!/^[6-9]\d{9}$/.test(customer.phone.trim())) return setOrderError("Enter a valid 10-digit Indian mobile number.");
    if (!customer.name.trim() || !customer.address.trim() || !customer.city.trim() || !/^\d{6}$/.test(customer.pincode.trim())) {
      return setOrderError("Please complete all required shipping details.");
    }
    if (!process.env.REACT_APP_RAZORPAY_KEY_ID) return setOrderError("Online payment is not configured yet. Please contact the store owner.");
    setPlacingOrder(true);
    try {
      const response = await fetch("/.netlify/functions/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purpose: "store_checkout",
          storeSlug: store.slug,
          customer: {
            name: customer.name.trim(), phone: customer.phone.trim(),
            email: customer.email.trim(), address: customer.address.trim(),
            city: customer.city.trim(), pincode: customer.pincode.trim()
          },
          items: cartItems.map((item) => ({ productId: item.id, quantity: Number(item.quantity) }))
        })
      });
      const created = await response.json();
      if (!response.ok) throw new Error(created.error || "Could not start payment.");
      const RazorpayCtor = await loadRazorpayCheckout();
      const checkout = new RazorpayCtor({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: created.amount,
        currency: "INR",
        name: "Vasthra",
        description: `Saree order from ${store.storeName || store.shopName || "reseller store"}`,
        order_id: created.razorpayOrderId,
        prefill: { name: customer.name.trim(), email: customer.email.trim(), contact: customer.phone.trim() },
        theme: { color: "#881337" },
        modal: { ondismiss: () => setPlacingOrder(false) },
        handler: async (payment) => {
          try {
            const verifyResponse = await fetch("/.netlify/functions/verify-payment", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                purpose: "store_checkout",
                draftId: created.draftId,
                razorpay_order_id: payment.razorpay_order_id,
                razorpay_payment_id: payment.razorpay_payment_id,
                razorpay_signature: payment.razorpay_signature
              })
            });
            const verified = await verifyResponse.json();
            if (!verifyResponse.ok) throw new Error(verified.error || "Payment verification failed.");
            const orderIds = verified.orderIds || [];
            setOrderSuccess(orderIds.join(", "));
            setInvoiceError("");
            setInvoiceData(verified.invoice || {
              orderIds,
              razorpayOrderId: payment.razorpay_order_id,
              razorpayPaymentId: payment.razorpay_payment_id,
              date: new Date().toLocaleString("en-IN"),
              customer: { ...customer },
              items: cartItems.map((item) => ({
                productName: item.name || item.title || "Saree",
                quantity: Number(item.quantity || 1),
                sellingPrice: Number(item.publicPrice || 0),
                lineTotal: Number(item.publicPrice || 0) * Number(item.quantity || 1),
              })),
              total,
            });
            setCart({});
            setCheckoutOpen(false);
            setCartOpen(false);
            setCustomer({ name: "", phone: "", email: "", address: "", city: "", pincode: "" });

          } catch (err) {
            console.error("Payment verification failed:", err);
            setOrderError("Payment was submitted, but verification is still pending. Do not pay again. Please contact support with payment ID: " + (payment.razorpay_payment_id || "not available"));
          } finally {
            setPlacingOrder(false);
          }
        }
      });
      checkout.on("payment.failed", (failure) => {
        setOrderError(failure.error?.description || "Payment failed. Please try again.");
        setPlacingOrder(false);
      });
      checkout.open();
    } catch (error) {
      console.error("Checkout failed:", error);
      setOrderError(error.message || "Could not start secure checkout.");
      setPlacingOrder(false);
    }
  };
  const productCategories = useMemo(
    () => [...new Set(products.map((product) => String(product.type || product.category || "").trim()).filter(Boolean))].sort(),
    [products],
  );
  const productFabrics = useMemo(
    () => [...new Set(products.map((product) => String(product.fabric || product.material || product.sareeType || "").trim()).filter(Boolean))].sort(),
    [products],
  );
  const filteredProducts = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();
    return products
      .filter((product) => Number(product.publicPrice) > 0)
      .filter((product) => {
        const text = [
          product.name, product.title, product.description, product.type,
          product.category, product.fabric, product.material, product.sareeType,
          ...(Array.isArray(product.tags) ? product.tags : [product.tags || ""]),
        ].filter(Boolean).join(" ").toLowerCase();
        const matchesSearch = !queryText || text.includes(queryText);
        // eslint-disable-next-line no-unused-vars
        const fabric = String(product.fabric || product.material || product.sareeType || "").toLowerCase();
        const matchesFabric = selectedFabric === "all" || text.includes(selectedFabric.toLowerCase());
        const category = String(product.type || product.category || "").toLowerCase();
        const matchesCategory = selectedCategory === "all" || category === selectedCategory.toLowerCase();
        const price = Number(product.publicPrice) || 0;
        const matchesPrice =
          priceFilter === "all" ||
          (priceFilter === "under-1000" && price < 1000) ||
          (priceFilter === "1000-2500" && price >= 1000 && price <= 2500) ||
          (priceFilter === "2500-5000" && price > 2500 && price <= 5000) ||
          (priceFilter === "above-5000" && price > 5000);
        return matchesSearch && matchesFabric && matchesCategory && matchesPrice;
      })
      .sort((a, b) => {
        if (sortOrder === "price-low") return Number(a.publicPrice) - Number(b.publicPrice);
        if (sortOrder === "price-high") return Number(b.publicPrice) - Number(a.publicPrice);
        if (sortOrder === "name") return String(a.name || a.title || "").localeCompare(String(b.name || b.title || ""));
        return 0;
      });
  }, [products, searchTerm, selectedFabric, selectedCategory, priceFilter, sortOrder]);
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const visibleProducts = filteredProducts.slice((safePage - 1) * pageSize, safePage * pageSize);
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedFabric("all");
    setSelectedCategory("all");
    setPriceFilter("all");
    setSortOrder("featured");
    setCurrentPage(1);
  };
  return (
    <main className="min-h-screen overflow-x-hidden bg-[#faf7f5] text-stone-800">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-300 text-rose-950">
              <Heart size={21} fill="currentColor" />
            </span>
            <span>
              <span className="block font-serif text-xl font-bold text-rose-950">
                Vasthra
              </span>
              <span className="block text-xs text-stone-500">
                A reseller boutique
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCartOpen(true)}
              className="relative inline-flex items-center gap-2 rounded-xl bg-rose-900 px-4 py-2.5 text-sm font-semibold text-white"
            >
              <ShoppingCart size={17} /> Cart{" "}
              {cartCount > 0 && (
                <span className="rounded-full bg-amber-300 px-2 py-0.5 text-xs text-rose-950">
                  {cartCount}
                </span>
              )}
            </button>
            <Link
              to="/"
              className="hidden items-center gap-2 text-sm font-semibold text-rose-900 hover:text-rose-700 sm:inline-flex"
            >
              <ArrowLeft size={16} /> Main website
            </Link>
          </div>
        </div>
      </header>
      {orderSuccess && (
        <div className="mx-auto mt-5 max-w-7xl px-4 sm:px-6">
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
            <CheckCircle2 className="mt-0.5 shrink-0" />
            <div>
              <h2 className="font-bold">Your order is placed!</h2>
              <p className="mt-1 text-sm">
                Order ID: <strong>{orderSuccess}</strong>. We’ll update you when
                your order is packed and shipped. Keep this order ID for
                reference.
              </p>
              {invoiceData && (
                <div className="mt-4">
                  <button
                    type="button"
                    disabled={downloadingInvoice}
                    onClick={async () => {
                      setDownloadingInvoice(true);
                      setInvoiceError("");
                      try {
                        await downloadInvoice(invoiceData);
                      } catch (error) {
                        console.error("Invoice download failed:", error);
                        setInvoiceError(error.message || "Could not download invoice. Please try again.");
                      } finally {
                        setDownloadingInvoice(false);
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-950 disabled:opacity-60"
                  >
                    {downloadingInvoice ? <LoaderCircle size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                    {downloadingInvoice ? "Preparing invoice..." : "Download Invoice (PDF)"}
                  </button>
                  {invoiceError && <p role="alert" className="mt-2 text-sm text-red-700">{invoiceError}</p>}
                </div>
              )}
            </div>
            <button
              className="ml-auto"
              onClick={() => setOrderSuccess("")}
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
      {loading && !store ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              className="mx-auto animate-spin text-rose-800"
              size={32}
            />
            <p className="mt-3 text-sm text-stone-500">Loading store...</p>
          </div>
        </div>
      ) : storeError && !store ? (
        <section className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-5 text-center">
          <Store size={36} className="text-rose-800" />
          <h1 className="mt-4 font-serif text-2xl text-rose-950">
            Store unavailable
          </h1>
          <p className="mt-2 text-sm leading-6 text-stone-600">{storeError}</p>
          <Link
            to="/"
            className="mt-6 rounded-xl bg-rose-900 px-5 py-3 text-sm font-semibold text-white"
          >
            Back to Vasthra
          </Link>
        </section>
      ) : (
        <>
          <section className="bg-gradient-to-br from-rose-950 via-rose-900 to-rose-800 text-white">
            <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-amber-200">
                <Store size={14} /> RESELLER COLLECTION
              </p>
              <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-tight sm:text-5xl">
                {store?.storeName || store?.shopName || "Saree Boutique"}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-rose-100 sm:text-base">
                {store?.tagline ||
                  store?.storeDescription ||
                  "Discover sarees for every occasion."}
              </p>
              <p className="mt-5 text-xs text-rose-200">
                Store link: /s/{store?.slug}
              </p>
            </div>
          </section>
          <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
            <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-800">
                  Curated for you
                </p>
                <h2 className="mt-1 font-serif text-3xl text-rose-950">
                  Our saree collection
                </h2>
              </div>
              <p className="text-sm text-stone-500">
                {filteredProducts.length}{" "}
                {filteredProducts.length === 1 ? "product" : "products"}
              </p>
            </div>
            {!storeError && products.length > 0 && (
              <div className="mb-5 rounded-xl border border-stone-200 bg-white p-3 sm:rounded-2xl sm:p-5">
                <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-rose-800 sm:mb-4 sm:text-xs sm:tracking-[0.16em]">
                  <SlidersHorizontal size={15} /> Search & filter
                </div>
                <label className="relative mb-3 block sm:mb-4">
                  <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => { setSearchTerm(event.target.value); setCurrentPage(1); }}
                    placeholder="Search saree name, fabric or style..."
                    aria-label="Search products"
                    className="w-full rounded-lg border border-stone-200 bg-[#fcfaf7] py-2.5 pl-9 pr-3 text-xs outline-none focus:border-rose-700 focus:ring-2 focus:ring-rose-100 sm:rounded-xl sm:py-3 sm:pl-10 sm:pr-4 sm:text-sm"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">
                  <label className="text-xs font-semibold text-stone-600">
                    Fabric
                    <select value={selectedFabric} onChange={(event) => { setSelectedFabric(event.target.value); setCurrentPage(1); }} className="mt-1 w-full min-w-0 rounded-lg border border-stone-200 bg-white px-2 py-2 text-xs sm:mt-1.5 sm:rounded-xl sm:px-3 sm:py-3 sm:text-sm">
                      <option value="all">All fabrics</option>
                      {productFabrics.map((fabric) => <option key={fabric} value={fabric}>{fabric}</option>)}
                      {![...productFabrics].some((fabric) => fabric.toLowerCase() === "silk") && <option value="silk">Silk</option>}
                      {![...productFabrics].some((fabric) => fabric.toLowerCase() === "cotton") && <option value="cotton">Cotton</option>}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-stone-600">
                    Category
                    <select value={selectedCategory} onChange={(event) => { setSelectedCategory(event.target.value); setCurrentPage(1); }} className="mt-1 w-full min-w-0 rounded-lg border border-stone-200 bg-white px-2 py-2 text-xs sm:mt-1.5 sm:rounded-xl sm:px-3 sm:py-3 sm:text-sm">
                      <option value="all">All categories</option>
                      {productCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-stone-600">
                    Price
                    <select value={priceFilter} onChange={(event) => { setPriceFilter(event.target.value); setCurrentPage(1); }} className="mt-1 w-full min-w-0 rounded-lg border border-stone-200 bg-white px-2 py-2 text-xs sm:mt-1.5 sm:rounded-xl sm:px-3 sm:py-3 sm:text-sm">
                      <option value="all">Any price</option>
                      <option value="under-1000">Under ₹1,000</option>
                      <option value="1000-2500">₹1,000–₹2,500</option>
                      <option value="2500-5000">₹2,501–₹5,000</option>
                      <option value="above-5000">Above ₹5,000</option>
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-stone-600">
                    Sort by
                    <select value={sortOrder} onChange={(event) => { setSortOrder(event.target.value); setCurrentPage(1); }} className="mt-1 w-full min-w-0 rounded-lg border border-stone-200 bg-white px-2 py-2 text-xs sm:mt-1.5 sm:rounded-xl sm:px-3 sm:py-3 sm:text-sm">
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: low to high</option>
                      <option value="price-high">Price: high to low</option>
                      <option value="name">Name: A to Z</option>
                    </select>
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 sm:mt-4 sm:gap-3">
                  <p className="text-[11px] text-stone-500 sm:text-xs">Showing {filteredProducts.length ? (safePage - 1) * pageSize + 1 : 0}–{Math.min(safePage * pageSize, filteredProducts.length)} of {filteredProducts.length}</p>
                  <button type="button" onClick={resetFilters} className="text-xs font-semibold text-rose-800 underline underline-offset-4 sm:text-sm">Clear filters</button>
                </div>
              </div>
            )}
            {storeError ? (
              <div
                role="alert"
                className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              >
                {storeError}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-5 py-14 text-center">
                <ShoppingBag className="mx-auto text-rose-300" size={36} />
                <h3 className="mt-4 font-serif text-xl text-rose-950">
                  Sarees coming soon
                </h3>
                <p className="mt-2 text-sm text-stone-500">
                  {products.length
                    ? "No products match these filters. Try clearing or changing your filters."
                    : "No sarees have been added to this reseller's store yet."}
                </p>
                {products.length > 0 && <button type="button" onClick={resetFilters} className="mt-4 rounded-xl bg-rose-900 px-5 py-2.5 text-sm font-semibold text-white">Clear filters</button>}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
                {visibleProducts.map((product) => {
                  const image =
                    product.imageUrl || product.image || product.photoURL;
                  return (
                    <article
                      key={product.id}
                      className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white transition hover:-translate-y-1 hover:shadow-lg sm:rounded-2xl"
                    >
                      <div className="flex aspect-[3/4] w-full items-center justify-center overflow-hidden bg-white p-1 sm:p-2">
                        {image ? (
                          <img
                            src={image}
                            alt={product.name || product.title || "Saree"}
                            loading="lazy"
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <ShoppingBag size={42} className="text-rose-300" />
                        )}
                      </div>
                      <div className="p-2.5 sm:p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-rose-800">
                          {product.type || product.category || "Saree"}
                        </p>
                        <h3 className="mt-1 line-clamp-2 break-words font-serif text-base text-rose-950 sm:text-xl">
                          {product.name || product.title || "Saree"}
                        </h3>
                        {product.description && (
                          <p className="mt-2 line-clamp-2 text-sm text-stone-500">
                            {product.description}
                          </p>
                        )}
                        <p className="mt-3 text-base font-bold text-rose-950 sm:mt-4 sm:text-lg">
                          {money(product.publicPrice)}
                        </p>
                        <div className="mt-3 grid grid-cols-1 gap-2 sm:mt-4">
                          <button
                            onClick={() => addToCart(product)}
                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-rose-900 px-2 py-2.5 text-[11px] font-bold text-white hover:bg-rose-950 sm:gap-2 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm"
                          >
                            <Plus size={17} /> Add to cart
                          </button>
                          <button
                            onClick={() => shareProduct(product)}
                            className="inline-flex items-center justify-center gap-1 rounded-lg border border-emerald-200 px-2 py-2.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-50 sm:gap-2 sm:rounded-xl sm:px-4 sm:py-3 sm:text-sm"
                          >
                            <MessageCircle size={17} /> Share saree on WhatsApp
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
            {!storeError && filteredProducts.length > pageSize && (
              <nav className="mt-8 flex items-center justify-center gap-3" aria-label="Product pages">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => { setCurrentPage((page) => Math.max(1, page - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="inline-flex items-center gap-1 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <span className="text-sm text-stone-600">Page {safePage} of {totalPages}</span>
                <button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => { setCurrentPage((page) => Math.min(totalPages, page + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="inline-flex items-center gap-1 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next <ChevronRight size={16} />
                </button>
              </nav>
            )}
          </section>
        </>
      )}
      <footer className="border-t border-stone-200 bg-white px-4 py-6 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} Vasthra
      </footer>

      {cartOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCartOpen(false);
          }}
        >
          <section className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
            <header className="flex items-center justify-between border-b p-5">
              <h2 className="font-serif text-2xl text-rose-950">Your cart</h2>
              <button
                onClick={() => setCartOpen(false)}
                aria-label="Close cart"
              >
                <X />
              </button>
            </header>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              {cartItems.length ? (
                cartItems.map((item) => (
                  <div key={item.id} className="flex gap-3 border-b pb-4">
                    <img
                      src={item.imageUrl || item.image || item.photoURL || ""}
                      alt=""
                      className="h-20 w-16 rounded-lg bg-rose-50 object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">
                        {item.name || item.title || "Saree"}
                      </p>
                      <p className="mt-1 text-sm text-rose-900">
                        {money(item.publicPrice)}
                      </p>
                      <div className="mt-2 flex items-center gap-3">
                        <button
                          onClick={() => changeQuantity(item.id, -1)}
                          className="rounded-lg border p-1"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={15} />
                        </button>
                        <span className="text-sm">{item.quantity}</span>
                        <button
                          onClick={() => changeQuantity(item.id, 1)}
                          className="rounded-lg border p-1"
                          aria-label="Increase quantity"
                        >
                          <Plus size={15} />
                        </button>
                        <button
                          onClick={() =>
                            changeQuantity(item.id, -item.quantity)
                          }
                          className="ml-auto text-stone-400 hover:text-red-600"
                          aria-label="Remove item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="font-bold">
                      {money(item.publicPrice * item.quantity)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="py-10 text-center text-sm text-stone-500">
                  Your cart is empty. Add a saree to get started.
                </p>
              )}
            </div>
            <div className="border-t p-5">
              <div className="mb-4 flex justify-between text-lg font-bold">
                <span>Total</span>
                <span>{money(total)}</span>
              </div>
              <button
                disabled={!cartItems.length}
                onClick={() => {
                  setCartOpen(false);
                  setCheckoutOpen(true);
                  setOrderError("");
                }}
                className="w-full rounded-xl bg-rose-900 px-4 py-3 font-bold text-white disabled:opacity-40"
              >
                Continue to checkout
              </button>
            </div>
          </section>
        </div>
      )}

      {checkoutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-3 sm:p-6">
          <section className="my-auto max-h-[95vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <header className="sticky top-0 flex items-center justify-between border-b bg-white p-5">
              <div>
                <h2 className="font-serif text-2xl text-rose-950">Checkout</h2>
                <p className="mt-1 text-xs text-stone-500">
                  Store: {store?.storeName || store?.slug}
                </p>
              </div>
              <button
                onClick={() => setCheckoutOpen(false)}
                aria-label="Close checkout"
              >
                <X />
              </button>
            </header>
            <form onSubmit={placeOrder} className="space-y-5 p-5 sm:p-6">
              <div className="rounded-xl bg-rose-50 p-4">
                <h3 className="font-semibold text-rose-950">Order summary</h3>
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="mt-2 flex justify-between gap-3 text-sm"
                  >
                    <span>
                      {item.name || item.title} × {item.quantity}
                    </span>
                    <span className="font-semibold">
                      {money(item.publicPrice * item.quantity)}
                    </span>
                  </div>
                ))}
                <div className="mt-3 flex justify-between border-t border-rose-200 pt-3 font-bold">
                  <span>Total to pay</span>
                  <span>{money(total)}</span>
                </div>
              </div>
              <div>
                <h3 className="mb-3 font-semibold text-stone-900">
                  Customer details
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm">
                    Full name
                    <input
                      required
                      maxLength={100}
                      value={customer.name}
                      onChange={(e) =>
                        setCustomer({ ...customer, name: e.target.value })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                    />
                  </label>
                  <label className="text-sm">
                    Mobile number
                    <input
                      required
                      inputMode="numeric"
                      maxLength={10}
                      value={customer.phone}
                      onChange={(e) =>
                        setCustomer({
                          ...customer,
                          phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                        })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                      placeholder="10-digit mobile"
                    />
                  </label>
                  <label className="text-sm sm:col-span-2">
                    Email (optional)
                    <input
                      type="email"
                      value={customer.email}
                      onChange={(e) =>
                        setCustomer({ ...customer, email: e.target.value })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                    />
                  </label>
                  <label className="text-sm sm:col-span-2">
                    Full delivery address
                    <textarea
                      required
                      maxLength={500}
                      rows={2}
                      value={customer.address}
                      onChange={(e) =>
                        setCustomer({ ...customer, address: e.target.value })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                    />
                  </label>
                  <label className="text-sm">
                    City
                    <input
                      required
                      maxLength={80}
                      value={customer.city}
                      onChange={(e) =>
                        setCustomer({ ...customer, city: e.target.value })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                    />
                  </label>
                  <label className="text-sm">
                    PIN code
                    <input
                      required
                      inputMode="numeric"
                      maxLength={6}
                      value={customer.pincode}
                      onChange={(e) =>
                        setCustomer({
                          ...customer,
                          pincode: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6),
                        })
                      }
                      className="mt-1 w-full rounded-xl border px-3 py-2.5"
                    />
                  </label>
                </div>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <h3 className="font-semibold text-amber-950">Secure online payment</h3>
                <p className="mt-1 text-sm text-amber-900">
                  Pay securely with Razorpay using UPI, cards, net banking or supported wallets.
                  Your order is confirmed only after server-side payment verification.
                </p>
                <div className="mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-white p-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-900"><ShieldCheck size={20} /></span>
                  <div><p className="font-semibold text-stone-900">Vasthra secure checkout</p><p className="text-xs text-stone-500">Total payable: {money(total)}</p></div>
                </div>
              </div>
              {orderError && (
                <p
                  role="alert"
                  className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  {orderError}
                </p>
              )}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCheckoutOpen(false)}
                  className="rounded-xl border px-5 py-3 text-sm font-semibold"
                >
                  Back to store
                </button>
                <button
                  type="submit"
                  disabled={placingOrder || !cartItems.length}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                >
                  {placingOrder ? (
                    <>
                      <LoaderCircle size={17} className="animate-spin" />{" "}
                      Placing order...
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={17} /> Pay securely with Razorpay
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs leading-5 text-stone-500">
                By placing this order, you submit your details and payment proof
                for review. Do not upload sensitive banking credentials or OTPs.
              </p>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
